import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import * as SplashScreen from 'expo-splash-screen';
import * as Notifications from 'expo-notifications';
import { useRouter } from 'expo-router';
import { useAuth } from '@/hooks/useAuth';
import { useTRPCClient } from '@/lib/trpc';
import { registerForPushNotificationsAsync } from '@/lib/notifications';
import { config } from '@/config/env';
import { clientLogger, trackRumAction } from '@/lib/datadog';

export function AuthBootController({ 
  timedOut, 
  onTimeoutChange 
}: { 
  timedOut: boolean;
  onTimeoutChange: (timedOut: boolean) => void;
}) {
  const { isLoading: isAuthLoading } = useAuth();
  const hasHiddenSplashRef = useRef(false);

  const hideSplashOnce = useCallback(() => {
    if (Platform.OS === 'web' || hasHiddenSplashRef.current) {
      return;
    }
    hasHiddenSplashRef.current = true;
    SplashScreen.hideAsync().catch(() => {});
  }, []);

  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (!hasHiddenSplashRef.current) {
        hideSplashOnce();
      }
    }, 10000);

    return () => clearTimeout(safetyTimeout);
  }, [hideSplashOnce]);

  useEffect(() => {
    if (!isAuthLoading) {
      if (timedOut) {
        onTimeoutChange(false);
      }
      hideSplashOnce();
      return;
    }

    if (timedOut) {
      hideSplashOnce();
      return;
    }

    const timeoutId = setTimeout(() => {
      onTimeoutChange(true);
      trackRumAction('auth_boot_timeout', { timeoutMs: config.authBootstrapTimeoutMs });
      hideSplashOnce();
    }, config.authBootstrapTimeoutMs);

    return () => clearTimeout(timeoutId);
  }, [timedOut, hideSplashOnce, isAuthLoading, onTimeoutChange]);

  return null;
}

export function PushNotificationHandler({ isReady }: { isReady: boolean }) {
  const { isAuthenticated } = useAuth();
  const trpcClient = useTRPCClient();
  const router = useRouter();

  const openNotificationTarget = useCallback((data: unknown) => {
    const href = data && typeof data === 'object' ? (data as Record<string, unknown>).href : null;
    if (typeof href === 'string' && /^\/league\/[^/?]+\/(?:scores|leaderboard)$/.test(href)) {
      router.push(href as never);
    }
  }, [router]);

  useEffect(() => {
    if (Platform.OS === 'web' || !isAuthenticated || !isReady) {
      return;
    }

    registerForPushNotificationsAsync().then((token) => {
      if (!token) {
        return;
      }

      trpcClient.users.updateMe.mutate({ pushToken: token }).catch((error) => {
        clientLogger.error('[PushHandler] Error: Failed to save push token', { error });
      });
    });
  }, [isAuthenticated, isReady, trpcClient]);

  useEffect(() => {
    if (Platform.OS === 'web' || !isAuthenticated || !isReady) {
      return;
    }
    const subscription = Notifications.addNotificationResponseReceivedListener((response) => {
      openNotificationTarget(response.notification.request.content.data);
    });
    void Notifications.getLastNotificationResponseAsync().then((response) => {
      if (response) openNotificationTarget(response.notification.request.content.data);
    });
    return () => subscription.remove();
  }, [isAuthenticated, isReady, openNotificationTarget]);

  return null;
}
